import React from 'react'
import {
  TableView,
  TableHeader,
  Column,
  TableBody,
  Row,
  Cell,
  StatusLight,
  Text,
  Button,
  Tooltip,
  TooltipTrigger
} from '@adobe/react-spectrum'
import { AlertTriangle } from 'lucide-react'

interface Device {
  id: string
  name: string
  status: 'active' | 'inactive' | 'error'
  lastSeen?: string
  location?: string
  type?: string
  engineer?: string
  calibrationDue?: string
  softwareVersion?: string
  totalTests?: number
}

interface DevicesTableProps {
  devices: Device[]
  translate: (key: string) => string
}

// Device type translation mapping
const deviceTypeMap: Record<string, string> = {
  'vdviipro': 'VDV II / PoE Pro',
  'vdvii': 'VDV II',
  'pocepro': 'PoE Pro',
  'anyvisionpro': 'AnyVision Pro',
  'anywarevm': 'AnyWARE VM',
  'anywaredmz': 'AnyWARE DMZ',
  'smart-hub': 'Smart Hub',
  'thermoprobe': 'Thermo Probe',
  'mobile': 'Mobile Device',
  'web-client': 'Web Client'
}

const DevicesTable: React.FC<DevicesTableProps> = ({ devices, translate }) => {
  const statusMap = {
    active: 'positive',
    inactive: 'neutral',
    error: 'negative'
  }

  const translateDeviceType = (deviceType?: string): string => {
    if (!deviceType) return translate('not_available')
    return deviceTypeMap[deviceType.toLowerCase()] || deviceType
  }

  const formatDateTime = (dateString?: string): string => {
    if (!dateString) return translate('not_available')
    try {
      const date = new Date(dateString)
      const dateFormatted = date.toLocaleDateString('en-GB', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
      const timeFormatted = date.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      })
      return `${dateFormatted} ${timeFormatted}`
    } catch {
      return dateString
    }
  }

  const formatDate = (dateString?: string): string => {
    if (!dateString) return translate('not_available')
    try {
      return new Date(dateString).toLocaleDateString('en-GB', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    } catch {
      return dateString
    }
  }

  const isCalibrationDueSoon = (calibrationDue?: string): boolean => {
    if (!calibrationDue) return false
    const dueDate = new Date(calibrationDue)
    const now = new Date()
    const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000)
    return dueDate <= thirtyDaysFromNow
  }

  const isCalibrationOverdue = (calibrationDue?: string): boolean => {
    if (!calibrationDue) return false
    const dueDate = new Date(calibrationDue)
    return dueDate < new Date()
  }

  const handleOpenDevice = (deviceId: string) => {
    window.location.href = `/device/${deviceId}`
  }

  return (
    <TableView
      aria-label="Devices table"
      selectionMode="multiple"
      selectionStyle="highlight"
      UNSAFE_className="home-table"
      UNSAFE_style={{ width: '100%' }}
    >
      <TableHeader>
        <Column key="name" allowsSorting>
          {translate('serial_number_header')}
        </Column>
        <Column key="type" allowsSorting>
          {translate('type_header')}
        </Column>
        <Column key="status" allowsSorting>
          {translate('status_header')}
        </Column>
        <Column key="engineer">
          {translate('engineer_header')}
        </Column>
        <Column key="calibrationDue" allowsSorting>
          {translate('calibration_due_header')}
        </Column>
        <Column key="lastSeen" allowsSorting>
          {translate('last_seen_header')}
        </Column>
        <Column key="actions">
          {translate('actions_header')}
        </Column>
      </TableHeader>
      <TableBody>
        {devices.map((device: Device) => (
          <Row key={device.id}>
            <Cell>
              <Text UNSAFE_style={{ fontWeight: 500 }}>{device.name}</Text>
            </Cell>
            <Cell>
              <Text>{translateDeviceType(device.type)}</Text>
            </Cell>
            <Cell>
              <StatusLight variant={statusMap[device.status] as any}>
                {device.status === 'active' ? translate('status_online') : device.status === 'inactive' ? translate('status_offline') : device.status}
              </StatusLight>
            </Cell>
            <Cell>
              <Text>{device.engineer || translate('unassigned')}</Text>
            </Cell>
            <Cell>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Text
                  UNSAFE_style={{
                    color: isCalibrationOverdue(device.calibrationDue)
                      ? '#ef4444'
                      : isCalibrationDueSoon(device.calibrationDue)
                      ? '#f59e0b'
                      : 'inherit'
                  }}
                >
                  {formatDate(device.calibrationDue)}
                </Text>
                {isCalibrationOverdue(device.calibrationDue) && (
                  <TooltipTrigger>
                    <Button variant="primary" isQuiet UNSAFE_style={{ padding: 0, minWidth: 'auto' }}>
                      <AlertTriangle size={12} style={{ color: '#ef4444' }} />
                    </Button>
                    <Tooltip>{translate('calibration_overdue_tooltip')}</Tooltip>
                  </TooltipTrigger>
                )}
                {!isCalibrationOverdue(device.calibrationDue) && isCalibrationDueSoon(device.calibrationDue) && (
                  <TooltipTrigger>
                    <Button variant="primary" isQuiet UNSAFE_style={{ padding: 0, minWidth: 'auto' }}>
                      <AlertTriangle size={12} style={{ color: '#f59e0b' }} />
                    </Button>
                    <Tooltip>{translate('calibration_due_soon_tooltip')}</Tooltip>
                  </TooltipTrigger>
                )}
              </div>
            </Cell>
            <Cell>
              <Text>{formatDateTime(device.lastSeen)}</Text>
            </Cell>
            <Cell>
              <div className="home-table-actions">
                <Button variant="secondary" UNSAFE_className="home-table-action home-table-action-view" onPress={() => handleOpenDevice(device.id)}>
                  {translate('general_view')}
                </Button>
              </div>
            </Cell>
          </Row>
        ))}
      </TableBody>
    </TableView>
  )
}

export default DevicesTable
